import React, { useState, useEffect } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { FaSearch, FaTimes, FaSlidersH, FaBan } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const headerColors = {
  search: {
    tint: "rgba(16,185,129,0.10)",
    border: "rgba(16,185,129,0.35)",
    icon: "#10b981",
  },
};

const SearchDrawer = ({ open, onClose, clients = [], selectedClient, onClientSelect }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeClientIndex, setActiveClientIndex] = useState(null);
  const [hoveredDisabledClientIndex, setHoveredDisabledClientIndex] = useState(null);
  const clientDropdownRef = React.useRef(null);

  // Filter clients based on search query (name or ID)
  const filteredClients = React.useMemo(() => {
    return clients.filter((client) => {
      const q = (searchQuery || "").trim().toLowerCase();
      if (!q) return true;

      const name = (client?.client_name || "").toLowerCase();
      const id = String(client?.client_id ?? "").toLowerCase();

      return name.includes(q) || id.includes(q);
    });
  }, [clients, searchQuery]);

  // Handle client selection
  const handleClientSelect = (client) => {
    if (onClientSelect) {
      onClientSelect(client);
    }
    setShowDropdown(false);
    setSearchQuery("");
    setActiveClientIndex(null);
    setHoveredDisabledClientIndex(null);
    onClose();
  };

  const handleClientSearchKeyDown = (e) => {
    const count = filteredClients.length;
    if (count === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!showDropdown) setShowDropdown(true);
      setActiveClientIndex((prev) => {
        const start = prev == null ? -1 : prev;
        return Math.min(start + 1, count - 1);
      });
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!showDropdown) setShowDropdown(true);
      setActiveClientIndex((prev) => {
        const start = prev == null ? count : prev;
        return Math.max(start - 1, 0);
      });
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      const indexToSelect = activeClientIndex == null ? 0 : activeClientIndex;
      const client = filteredClients[indexToSelect];
      if (!client) return;

      const hasNoId = client.client_id == null || client.client_id === 0 || client.client_id === "";
      if (hasNoId) {
        setHoveredDisabledClientIndex(indexToSelect);
        return;
      }

      handleClientSelect(client);
    }
  };

  // When dropdown opens or query changes, pick a deterministic initial highlight.
  useEffect(() => {
    if (!open || !showDropdown) return;
    if (filteredClients.length === 0) {
      setActiveClientIndex(null);
      return;
    }

    const selectedIndex =
      selectedClient?.client_id != null
        ? filteredClients.findIndex((c) => c.client_id === selectedClient.client_id)
        : -1;

    setActiveClientIndex(selectedIndex !== -1 ? selectedIndex : 0);
  }, [open, showDropdown, filteredClients, selectedClient?.client_id]);

  // Align the disabled red icon with the current keyboard highlight.
  useEffect(() => {
    if (!showDropdown) return;
    if (activeClientIndex == null) {
      setHoveredDisabledClientIndex(null);
      return;
    }

    const client = filteredClients[activeClientIndex];
    if (!client) return;
    const hasNoId = client.client_id == null || client.client_id === 0 || client.client_id === "";
    setHoveredDisabledClientIndex(hasNoId ? activeClientIndex : null);
  }, [activeClientIndex, showDropdown, filteredClients]);

  // Keep the keyboard-highlighted client visible inside the scrollable dropdown.
  useEffect(() => {
    if (!showDropdown || activeClientIndex == null) return;

    const list = clientDropdownRef.current;
    if (!list) return;

    const option = list.querySelector(`#mobile-client-option-${activeClientIndex}`);
    if (!option) return;

    option.scrollIntoView({ block: "nearest" });
  }, [activeClientIndex, showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown || !open) return;

    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector("[data-mobile-search]");
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowDropdown(false);
        setActiveClientIndex(null);
        setHoveredDisabledClientIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown, open]);

  // Reset search when drawer closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setShowDropdown(false);
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* BACKDROP */}
          <motion.div
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              left: 0,
              background: "black",
              zIndex: 99999,
            }}
          />

          {/* SLIDE DOWN SEARCH BAR */}
          <motion.div
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              position: "fixed",
              top: 10,
              left: "5%",
              width: "90%",
              background: "var(--color-bg-card)",
              borderRadius: 18,
              boxShadow: "var(--shadow-md)",
              padding: 14,
              zIndex: 99999,
            }}
            data-mobile-search
          >
            <div className="d-flex justify-content-between align-items-center" style={{ position: "relative" }}>
              {/* SEARCH INPUT */}
              <InputGroup style={{ flex: 1 }}>
                <InputGroup.Text
                  style={{
                    background: headerColors.search.tint,
                    border: `1px solid ${headerColors.search.border}`,
                    borderTopLeftRadius: 14,
                    borderBottomLeftRadius: 14,
                    color: headerColors.search.icon,
                  }}
                >
                  <FaSearch size={16} />
                </InputGroup.Text>
                <Form.Control
                  placeholder={selectedClient ? selectedClient.client_name : "Search clients..."}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (clients.length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  onFocus={() => {
                    if (clients.length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  onKeyDown={handleClientSearchKeyDown}
                  autoFocus
                  style={{
                    background: "var(--color-bg-card)",
                    border: `1px solid ${headerColors.search.border}`,
                    borderTopRightRadius: 14,
                    borderBottomRightRadius: 14,
                    color: "var(--color-text)",
                    boxShadow: `
                      0 2px 4px rgba(0,0,0,0.15),
                      inset 0 1px 0 rgba(255,255,255,0.15)
                    `,
                  }}
                  aria-expanded={showDropdown}
                  aria-activedescendant={
                    activeClientIndex != null ? `mobile-client-option-${activeClientIndex}` : undefined
                  }
                />
                {selectedClient && (
                  <InputGroup.Text
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onClientSelect) {
                        onClientSelect(null);
                      }
                      setSearchQuery("");
                      setShowDropdown(false);
                      setActiveClientIndex(null);
                      setHoveredDisabledClientIndex(null);
                    }}
                    style={{
                      border: "none",
                      borderTopRightRadius: 14,
                      borderBottomRightRadius: 14,
                      cursor: "pointer",
                      background: "var(--color-bg-card)",
                    }}
                  >
                    <FaTimes size={12} color="var(--color-text-light)" />
                  </InputGroup.Text>
                )}
              </InputGroup>

              {/* SETTINGS ICON */}
              <button
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  border: "none",
                  background: "rgba(112, 0, 255, 0.08)",
                  color: "var(--color-primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: 10,
                }}
              >
                <FaSlidersH size={16} />
              </button>

              {/* CLOSE BUTTON */}
              <button
                onClick={onClose}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  border: "none",
                  marginLeft: 8,
                  background: "rgba(255,0,0,0.08)",
                  color: "#e63737",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaTimes size={16} />
              </button>

              {/* DROPDOWN LIST */}
              {showDropdown && (
                <div
                  ref={clientDropdownRef}
                  className="client-dropdown-list"
                  onMouseLeave={() => {
                    setActiveClientIndex(null);
                    setHoveredDisabledClientIndex(null);
                  }}
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    marginTop: 8,
                    background: "var(--color-bg-card)",
                    borderRadius: 14,
                    boxShadow: "var(--shadow-md)",
                    maxHeight: 300,
                    overflowY: "auto",
                    zIndex: 100000,
                    border: "1px solid var(--color-border)",
                  }}
                >
                  {filteredClients.length === 0 ? (
                    <div style={{ padding: "12px 16px", color: "var(--color-text-light)", textAlign: "center" }}>
                      {clients.length === 0 ? "No clients available" : "No clients match your search"}
                    </div>
                  ) : (
                    filteredClients.map((client, index) => {
                      const hasNoId = client.client_id == null || client.client_id === 0 || client.client_id === "";
                      const showRedIcon = hasNoId && hoveredDisabledClientIndex === index;
                      const isActive = activeClientIndex === index;
                      return (
                        <div
                          key={client.client_id != null && client.client_id !== "" ? client.client_id : `client-${index}`}
                          onClick={() => !hasNoId && handleClientSelect(client)}
                          id={`mobile-client-option-${index}`}
                          style={{
                            padding: "12px 16px",
                            cursor: hasNoId ? "not-allowed" : "pointer",
                            borderBottom: "1px solid var(--color-border)",
                            transition: "background 0.2s",
                            opacity: hasNoId ? 0.5 : 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: isActive ? "#f2f2f2" : "transparent",
                          }}
                          onMouseEnter={(e) => {
                            setActiveClientIndex(index);
                            if (hasNoId) setHoveredDisabledClientIndex(index);
                            else setHoveredDisabledClientIndex(null);
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: isActive ? 700 : 500,
                                color: hasNoId ? "var(--color-text-light)" : "var(--color-text)",
                              }}
                            >
                              {client.client_name}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--color-text-light)", marginTop: 2 }}>ID: {client.client_id ?? "—"}</div>
                          </div>
                          {showRedIcon && <FaBan size={18} color="#e74a3b" style={{ flexShrink: 0 }} />}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SearchDrawer;
